import { useDispatch, useSelector } from 'react-redux';
import {
    setTemplatesRes, setTemplatesLoading, setTemplatesError,
    setTemplateByIdRes, appendTemplatesData, setTemplatesLoadingMore,
    resetTemplatesPagination, setTemplatesSearchTerm
} from '../redux/slices/templates';
import axios from '../utils/axiosConfig';
import { notify } from 'react-notify-toast';
import store from '../redux/store';

const useTemplateService = () => {
    const dispatch = useDispatch();

    const showError  = (error:any) =>{
        if(error?.response?.data?.error){
            notify.show(error.response.data.error, "error", 3000);
        }
        if(typeof error?.response?.data?.detail === "string" && error?.response?.data?.detail){
            notify.show(error.response.data.detail, "error", 3000);
        }
        if(error.message){
            notify.show(error.message, "error", 3000);
        }
    }

    const fetchAllTemplates = async (reset: boolean = true, pageNumber?: number, searchTerm: string = '') => {
        if (reset) {
            if (pageNumber) {
                // If a specific page is requested, don't reset pagination state
                dispatch(setTemplatesLoading(true));
            } else {
                dispatch(resetTemplatesPagination());
                dispatch(setTemplatesLoading(true));
            }
        } else {
            dispatch(setTemplatesLoadingMore(true));
        }

        try {
            const { perPage = 10 } = store.getState().templates;
            const currentPage = reset 
                ? (pageNumber || 1) // Use the specified page number or default to 1
                : store.getState().templates.page + 1;
            
            // Include search parameter in API request
            const response = await axios.get<any>(
                `/templates?page=${currentPage}&per_page=${perPage}&search=${encodeURIComponent(searchTerm)}`
            );
            
            if (reset) {
                dispatch(setTemplatesRes(response.data));
            } else {
                dispatch(appendTemplatesData(response.data));
            }
        } catch (error) {
            showError(error)
            dispatch(setTemplatesError(error));
        } finally {
            if (reset) {
                dispatch(setTemplatesLoading(false));
            } else {
                dispatch(setTemplatesLoadingMore(false));
            }
        }
    };

    const fetchMoreTemplates = async () => {
        const { templatesLoadingMore, hasMore, searchTerm = '' } = store.getState().templates;
        
        if (!templatesLoadingMore && hasMore) {
            await fetchAllTemplates(false, undefined, searchTerm);
        }
    };
    
    const setSearchTerm = (term: string) => {
        dispatch(setTemplatesSearchTerm(term));
    };

    const getTemplateById = async (project_id?: any) => {
        dispatch(setTemplatesLoading(true));
        try {
            const response = await axios.get<any[]>(`/project/${project_id}`);
            dispatch(setTemplateByIdRes(response?.data));
        } catch (error) {
            showError(error)
            dispatch(setTemplatesError(error));
        } finally {
            dispatch(setTemplatesLoading(false));
        }
    };
    const saveTemplate = async (payload:any) => {
        dispatch(setTemplatesLoading(true));
        try {
            const response = await axios.post<any>(`/template`,payload);
            if(response.data && response.data.message) {
                notify.show(response.data.message, "success", 3000);
            }
            await fetchAllTemplates();
        } catch (error) {
            showError(error)
            dispatch(setTemplatesError(error));
        } finally {
            dispatch(setTemplatesLoading(false));
        }
    };
    const updateTemplate = async (payload:any) => {
        dispatch(setTemplatesLoading(true));
        try {
            const response = await axios.put<any>(`/template`,payload);
            if(response.data && response.data.message) {
                notify.show(response.data.message, "success", 3000);
            }
            await fetchAllTemplates();
        } catch (error) {
            showError(error)
            dispatch(setTemplatesError(error));
        } finally {
            dispatch(setTemplatesLoading(false));
        }
    };

    const deleteTemplate = async (payload:any) => {
        dispatch(setTemplatesLoading(true));
        try {
            const response = await axios.delete<any>(`/template/${payload.template_id}`);
            if(response.data && response.data.message) {
                notify.show(response.data.message, "success", 3000);
            }
            await fetchAllTemplates();
        } catch (error) {
            showError(error)
            dispatch(setTemplatesError(error));
        } finally {
            dispatch(setTemplatesLoading(false));
        }
    };

    return { 
        fetchAllTemplates, saveTemplate, updateTemplate,
        deleteTemplate, getTemplateById, fetchMoreTemplates,
        setSearchTerm
     };
};

export default useTemplateService;